"use client";
import React, { useState } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mail, ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { forgotPassword } from "@/src/actions/auth-session";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
      setIsSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link
            href="/signin"
            className="inline-flex items-center gap-2 text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">
              {t("auth.forgotPassword.backToSignIn")}
            </span>
          </Link>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <GlassCard className="p-8 md:p-12">
            {!isSubmitted ? (
              <>
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-walnut-900 dark:text-white mb-2">
                    {t("auth.forgotPassword.title")}
                  </h1>
                  <p className="text-walnut-700 dark:text-walnut-300">
                    {t("auth.forgotPassword.subtitle")}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-walnut-900 dark:text-white mb-2"
                    >
                      {t("auth.forgotPassword.email")}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("auth.forgotPassword.emailPlaceholder")}
                        required
                        className="w-full pl-12 pr-4 py-3.5 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 focus:ring-2 focus:ring-walnut-500/20 transition-all"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    pill
                    className="w-full"
                  >
                    {t("auth.forgotPassword.sendResetLink")}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </form>
              </>
            ) : (
              <>
                {/* Success Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Success Message */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-walnut-900 dark:text-white mb-2">
                    {t("auth.forgotPassword.successTitle")}
                  </h1>
                  <p className="text-walnut-700 dark:text-walnut-300">
                    {t("auth.forgotPassword.successSubtitle")}
                  </p>
                  <p className="text-walnut-900 dark:text-white font-semibold mt-2">
                    {email}
                  </p>
                </div>

                {/* Instructions */}
                <div className="space-y-4 mb-8">
                  <div className="p-4 rounded-[20px] backdrop-blur-[5px] bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10">
                    <p className="text-sm text-walnut-700 dark:text-walnut-300 text-center">
                      {t("auth.forgotPassword.resendPrompt")}{" "}
                      <Button
                        variant="link"
                        onClick={() => setIsSubmitted(false)}
                        className="inline p-0 h-auto text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-semibold"
                      >
                        {t("auth.forgotPassword.tryAnotherEmail")}
                      </Button>
                    </p>
                  </div>
                </div>

                {/* Back to Sign In */}
                <Link
                  href="/signin"
                  className="w-full px-8 py-4 rounded-[33px] backdrop-blur-[5px] bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 text-walnut-700 dark:text-walnut-300 font-semibold hover:bg-white/20 transition-all duration-300 hover:scale-[1.02] inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  {t("auth.forgotPassword.backToSignIn")}
                </Link>
              </>
            )}
          </GlassCard>
        </motion.div>

        {/* Help Text */}
        {!isSubmitted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-center"
          >
            <p className="text-sm text-walnut-700 dark:text-walnut-300">
              {t("auth.forgotPassword.needHelp")}{" "}
              <Link
                href="/contact"
                className="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-semibold transition-colors"
              >
                {t("auth.forgotPassword.contactSupport")}
              </Link>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};
